import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, X, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;

const FIELDS = [
  { name: "name", label: "Customer Name", type: "text", required: true, autoComplete: "name" },
  { name: "phone", label: "Phone Number", type: "tel", required: true, autoComplete: "tel" },
  { name: "email", label: "Email", type: "email", required: true, autoComplete: "email" },
  { name: "vehicle_year", label: "Vehicle Year", type: "text", required: true, placeholder: "e.g. 2019" },
  { name: "make", label: "Make", type: "text", required: true, placeholder: "e.g. Ford" },
  { name: "model", label: "Model", type: "text", required: true, placeholder: "e.g. F-250" },
  { name: "engine", label: "Engine", type: "text", required: true, placeholder: "e.g. 6.7L Power Stroke" },
  { name: "vin", label: "VIN (optional)", type: "text", required: false },
  { name: "mileage", label: "Mileage", type: "text", required: true, placeholder: "e.g. 85,000" },
];

const inputCls =
  "w-full bg-zinc-900/60 border border-zinc-800 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#FF3B30] transition-colors duration-200";

export const RequestForm = () => {
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map((f) => [f.name, ""])));
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((prev) => [...prev, ...files].slice(0, MAX_PHOTOS));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      photos.forEach((p) => data.append("photos", p));
      await axios.post(`${API}/service-requests`, data);
      setSubmitted(true);
      window.scrollTo({ top: document.getElementById("request").offsetTop, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="request" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-zinc-950 border-y border-zinc-800 noise relative" data-testid="request-section">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
        <Reveal className="lg:col-span-2">
          <img src="/logo.png" alt="Ascencio Auto Repair — official logo" className="h-16 w-auto mb-8" draggable="false" data-testid="request-logo" />
          <p className="text-[#FF3B30] text-xs uppercase tracking-[0.35em] font-semibold mb-4">08 — Request Service</p>
          <h2 className="font-oswald font-bold uppercase tracking-tight text-4xl sm:text-5xl leading-[1.02]" data-testid="request-heading">
            Tell Us What<br /><span className="text-zinc-500">Your Vehicle Needs.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base md:text-lg">
            Fill out the form and we'll contact you as soon as possible. Add photos of the
            vehicle or the problem — they help us diagnose faster.
          </p>
          <p className="mt-6 text-sm text-zinc-500">
            Prefer email? <a href="mailto:ascencio12autorepair@gmail.com" className="text-[#FF3B30] hover:underline" data-testid="request-email-link">ascencio12autorepair@gmail.com</a>
          </p>
        </Reveal>

        <div className="lg:col-span-3">
          {submitted ? (
            <div className="border border-zinc-800 bg-[#0A0A0A] p-10 md:p-14 text-center" data-testid="request-success-message">
              <CheckCircle2 className="w-12 h-12 text-[#FF3B30] mx-auto" strokeWidth={1.5} />
              <h3 className="mt-6 font-oswald uppercase tracking-tight text-2xl">Request Received</h3>
              <p className="mt-4 text-zinc-300 max-w-md mx-auto">
                Thank you for contacting Ascencio Auto Repair. We received your service request
                and will contact you as soon as possible.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm(Object.fromEntries(FIELDS.map((f) => [f.name, ""]))); setPhotos([]); }}
                data-testid="request-new-btn"
                className="mt-8 border border-zinc-600 px-8 py-3 font-oswald uppercase tracking-[0.15em] text-sm hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors duration-200"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-zinc-800 bg-[#0A0A0A] p-6 md:p-10" data-testid="request-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FIELDS.map((f) => (
                  <div key={f.name}>
                    <label htmlFor={`field-${f.name}`} className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                      {f.label} {f.required && <span className="text-[#FF3B30]">*</span>}
                    </label>
                    <input
                      id={`field-${f.name}`}
                      data-testid={`field-${f.name}`}
                      type={f.type}
                      required={f.required}
                      value={form[f.name]}
                      onChange={set(f.name)}
                      placeholder={f.placeholder || ""}
                      autoComplete={f.autoComplete || "off"}
                      className={inputCls}
                    />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label htmlFor="field-description" className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Service Needed / Problem Description <span className="text-[#FF3B30]">*</span>
                  </label>
                  <textarea
                    id="field-description"
                    data-testid="field-description"
                    required
                    rows={4}
                    value={form.description || ""}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Describe the problem, noises, warning lights, or the service you need..."
                    className={`${inputCls} resize-y`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Photos of Vehicle / Problem (optional, up to {MAX_PHOTOS})
                  </label>
                  <label
                    htmlFor="field-photos"
                    data-testid="photo-upload-dropzone"
                    className="flex items-center justify-center gap-3 border border-dashed border-zinc-700 hover:border-[#FF3B30] transition-colors duration-200 px-4 py-8 cursor-pointer text-zinc-400 text-sm"
                  >
                    <UploadCloud className="w-5 h-5 text-[#FF3B30]" strokeWidth={1.5} />
                    Tap to add photos (JPG, PNG, HEIC — max 8MB each)
                  </label>
                  <input id="field-photos" data-testid="field-photos" type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
                  {photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3" data-testid="photo-preview-list">
                      {photos.map((p, i) => (
                        <div key={i} className="relative w-20 h-20 border border-zinc-700">
                          <img src={URL.createObjectURL(p)} alt={`Upload preview ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            aria-label={`Remove photo ${i + 1}`}
                            data-testid={`photo-remove-${i}`}
                            onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                            className="absolute -top-2 -right-2 bg-[#FF3B30] text-white p-0.5"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="request-submit-btn"
                className="mt-8 w-full bg-[#FF3B30] text-white font-oswald uppercase tracking-[0.2em] text-lg py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
              >
                {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                {submitting ? "Sending..." : "Request Service"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
